import { useEffect, useState, useRef } from "react";
import { Radar, Search, Phone, ShieldCheck, Sparkles, AlertTriangle } from "lucide-react";

const STEPS = [
  { icon: Search, text: "Menghubungkan ke sumber data & menyiapkan kueri..." },
  { icon: Radar, text: "Memindai bisnis sesuai kata kunci & lokasi..." },
  { icon: Phone, text: "Mengekstrak nomor telepon, email & kontak..." },
  { icon: ShieldCheck, text: "Memvalidasi & membersihkan data duplikat..." },
  { icon: Sparkles, text: "Hampir selesai, menyiapkan hasil terbaik untukmu..." },
];

const FACTS = [
  "Jumlah hasil bisa lebih sedikit dari target — kami hanya ambil data yang valid & relevan.",
  "Data berkualitas butuh waktu. Sumber dengan banyak bisnis bisa lebih lama diproses.",
  "Kami menyaring duplikat otomatis agar lead-mu bersih dan siap pakai.",
  "Tips: gunakan kata kunci spesifik (mis. 'klinik gigi', 'kafe kekinian') untuk hasil lebih tajam.",
];

// SSE event → human-readable messages
const EVENT_MESSAGES = {
  start: "Memulai pencarian...",
  cache_hit: "Memuat dari cache — data sudah pernah dicari sebelumnya!",
  actor_start: "Menjalankan scraper...",
  actor_error: "Actor mengalami gangguan, beralih ke alternatif...",
  actor_done: "Selesai memindai satu sumber.",
  token_failover: "Token habis, mencoba token cadangan...",
  done: "Pencarian selesai!",
  error: "Terjadi kesalahan.",
};

export default function ScrapingLoader({ source = "google_maps", progress = null }) {
  const [step, setStep] = useState(0);
  const [fact, setFact] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const s = setInterval(() => setStep((v) => (v + 1) % STEPS.length), 2200);
    const f = setInterval(() => setFact((v) => (v + 1) % FACTS.length), 3800);
    return () => { clearInterval(s); clearInterval(f); };
  }, []);

  // SSE progress → dynamic message
  useEffect(() => {
    if (!progress?.event) return;
    const msg = EVENT_MESSAGES[progress.event] || progress.event;

    // For actor_start, show actor count if available
    if (progress.event === "actor_start" && progress.index && progress.total_actors) {
      setCurrentMessage(`Menjalankan scraper (${progress.index}/${progress.total_actors})...`);
    } else if (progress.event === "actor_done" && progress.count !== undefined) {
      setCurrentMessage(`Sumber menghasilkan ${progress.count} hasil`);
      if (progress.count > 0) {
        timerRef.current = setTimeout(() => setCurrentMessage(null), 2500);
      }
    } else {
      setCurrentMessage(msg);
    }
    return () => clearTimeout(timerRef.current);
  }, [progress]);

  const ActiveIcon = STEPS[step].icon;

  return (
    <div className="card-soft p-8 sm:p-12 flex flex-col items-center text-center" data-testid="scraping-loader">
      <style>{`
        @keyframes radarSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulseRing { 0% { transform: scale(.6); opacity: .8; } 80%,100% { transform: scale(2.2); opacity: 0; } }
        @keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
      `}</style>

      {/* Radar visual */}
      <div className="relative w-32 h-32 mb-6">
        <span className="absolute inset-0 rounded-full bg-sky-400/30" style={{ animation: "pulseRing 2.4s ease-out infinite" }} />
        <span className="absolute inset-0 rounded-full bg-sky-400/20" style={{ animation: "pulseRing 2.4s ease-out infinite", animationDelay: "1.2s" }} />
        <div className="absolute inset-0 rounded-full border-2 border-sky-100" />
        <div className="absolute inset-3 rounded-full border border-sky-100" />
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{ animation: "radarSweep 2.6s linear infinite" }}>
          <div className="absolute top-1/2 left-1/2 w-1/2 h-1/2 origin-top-left"
            style={{ background: "conic-gradient(from 0deg, rgba(14,165,233,.45), transparent 90deg)" }} />
        </div>
        <div className="absolute inset-0 grid place-items-center">
          <div className="w-12 h-12 rounded-2xl bg-sky-500 grid place-items-center shadow-lg shadow-sky-200" style={{ animation: "floaty 2.2s ease-in-out infinite" }}>
            <ActiveIcon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      <h3 className="font-display text-lg font-extrabold text-slate-800">Sedang mengumpulkan lead…</h3>

      {/* Dynamic message — prefer SSE progress over static step text */}
      <p className="text-sm text-sky-600 font-medium mt-1.5 min-h-[20px] transition-all" key={currentMessage || `step-${step}`}>
        {currentMessage || STEPS[step].text}
      </p>

      {/* Step dots */}
      <div className="flex items-center gap-1.5 mt-4">
        {STEPS.map((_, i) => (
          <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-6 bg-sky-500" : "w-1.5 bg-slate-200"}`} />
        ))}
      </div>

      {/* Rotating fact */}
      <div className="mt-6 max-w-md rounded-xl bg-slate-50 border border-slate-100 px-4 py-3" key={`f-${fact}`}>
        <p className="text-xs text-slate-500 leading-relaxed"><span className="font-semibold text-slate-600">Tahukah kamu?</span> {FACTS[fact]}</p>
      </div>
    </div>
  );
}
